/**
 * Registry of AI tools surfaced on the super-admin AI Tooling page.
 *
 * Each tool's editable configuration is persisted to Firestore at
 * `ai_tooling/{tool.id}`. The page renders one tab per entry below and the
 * tab body is a generic config form (model / system / user / JSON schema)
 * driven by these defaults.
 *
 * To add a new tool, append an entry to `AI_TOOLS` — the page will pick it
 * up automatically. Tool ids are lowercase, hyphenated, and used directly as
 * the Firestore document id.
 */

import {
	DEFAULT_FIELDS as ENQUIRY_DEFAULT_FIELDS,
	DEFAULT_PREDICTIONS as ENQUIRY_DEFAULT_PREDICTIONS,
	DEFAULT_SYSTEM_INSTRUCTIONS as ENQUIRY_DEFAULT_SYSTEM,
	DEFAULT_USER_INSTRUCTIONS as ENQUIRY_DEFAULT_USER,
	buildSchemaFromFields,
} from "../enquiry-ingest/registry"

import { AI_MODELS } from "../communications/_components/ai-tools/registry"

import {
	DOCUMENT_CLASSIFIER_MODEL,
	DOCUMENT_CLASSIFIER_TEMPERATURE,
	DOCUMENT_CLASSIFIER_SYSTEM_PROMPT,
	DOCUMENT_CLASSIFIER_USER_PROMPT,
	DOCUMENT_CLASSIFIER_SCHEMA,
	DOCUMENT_CLASSIFIER_SCHEMA_NAME,
} from "./_seeds/document-classifier"

import {
	DEFAULT_ACCOMMODATION_MODEL,
	DEFAULT_ACCOMMODATION_SYSTEM_PROMPT,
	DEFAULT_ACCOMMODATION_USER_PROMPT,
} from "../../utils/agent-parameters/supplier-documents/extraction/accommodation-parameters"
import {
	ACCOMMODATION_FIELD_JSON_SCHEMA,
	ACCOMMODATION_FIELD_JSON_SCHEMA_NAME,
} from "../../utils/agent-parameters/supplier-documents/extraction/accommodation-schema"
import {
	DEFAULT_GREEN_FEES_MODEL,
	DEFAULT_GREEN_FEES_SYSTEM_PROMPT,
	DEFAULT_GREEN_FEES_USER_PROMPT,
} from "../../utils/agent-parameters/supplier-documents/extraction/green-fees-parameters"
import {
	GREEN_FEES_FIELD_JSON_SCHEMA,
	GREEN_FEES_FIELD_JSON_SCHEMA_NAME,
} from "../../utils/agent-parameters/supplier-documents/extraction/green-fees-schema"
import {
	DEFAULT_PACKAGES_MODEL,
	DEFAULT_PACKAGES_SYSTEM_PROMPT,
	DEFAULT_PACKAGES_USER_PROMPT,
} from "../../utils/agent-parameters/supplier-documents/extraction/packages-parameters"
import {
	PACKAGES_FIELD_JSON_SCHEMA,
	PACKAGES_FIELD_JSON_SCHEMA_NAME,
} from "../../utils/agent-parameters/supplier-documents/extraction/packages-schema"

export { AI_MODELS }

export const AI_TOOLING_COLLECTION = "ai_tooling"

/**
 * Pretty-printed JSON schema seed for the enquiry classifier. Built from the
 * same field / prediction defaults the existing Enquiry Ingest page uses, so
 * the new page is the canonical store from day one without a behaviour
 * regression for whoever copies the schema back into a runtime config.
 */
const ENQUIRY_CLASSIFIER_SCHEMA = buildSchemaFromFields(
	ENQUIRY_DEFAULT_FIELDS,
	ENQUIRY_DEFAULT_PREDICTIONS
)

export const AI_TOOLS = [
	{
		id: "enquiry-classifier",
		label: "Enquiry classifier",
		description:
			"Parses inbound enquiry emails into the structured fields used by the sales triage UI. Mirrors the in-page Enquiry Ingest dev tab.",
		defaults: {
			model: "gpt-4.1-mini",
			temperature: 0,
			system_instructions: ENQUIRY_DEFAULT_SYSTEM,
			user_instructions: ENQUIRY_DEFAULT_USER,
			json_schema: ENQUIRY_CLASSIFIER_SCHEMA,
			json_schema_name: "enquiry_ingest",
			json_schema_strict: true,
		},
	},
	{
		id: "document-classifier",
		label: "Supplier document classifier",
		description:
			"Classifies inbound supplier documents (rate cards, stop sales, contracts, supplier information) and extracts the structured payload defined by the supplier_document_parse schema. Ported from the rate-card-classifier branch.",
		defaults: {
			model: DOCUMENT_CLASSIFIER_MODEL,
			temperature: DOCUMENT_CLASSIFIER_TEMPERATURE,
			system_instructions: DOCUMENT_CLASSIFIER_SYSTEM_PROMPT,
			user_instructions: DOCUMENT_CLASSIFIER_USER_PROMPT,
			json_schema: DOCUMENT_CLASSIFIER_SCHEMA,
			json_schema_name: DOCUMENT_CLASSIFIER_SCHEMA_NAME,
			json_schema_strict: true,
		},
	},
	{
		id: "accommodation-reader",
		label: "Accommodation reader",
		description:
			"Specialist that extracts accommodation rate lines + supplements (room types, board basis, occupancy, periods, prices) from supplier rate cards. Runs in parallel with the green-fees reader after a document is classified as a rate card.",
		defaults: {
			model: DEFAULT_ACCOMMODATION_MODEL,
			temperature: 0,
			system_instructions: DEFAULT_ACCOMMODATION_SYSTEM_PROMPT,
			user_instructions: DEFAULT_ACCOMMODATION_USER_PROMPT,
			json_schema: ACCOMMODATION_FIELD_JSON_SCHEMA,
			json_schema_name: ACCOMMODATION_FIELD_JSON_SCHEMA_NAME,
			json_schema_strict: false,
		},
	},
	{
		id: "green-fees-reader",
		label: "Green fees reader",
		description:
			"Specialist that extracts green-fee rate lines + supplements (courses, tee windows, periods, prices, buggy/replay inclusions) from supplier rate cards. Runs in parallel with the accommodation reader after a document is classified as a rate card.",
		defaults: {
			model: DEFAULT_GREEN_FEES_MODEL,
			temperature: 0,
			system_instructions: DEFAULT_GREEN_FEES_SYSTEM_PROMPT,
			user_instructions: DEFAULT_GREEN_FEES_USER_PROMPT,
			json_schema: GREEN_FEES_FIELD_JSON_SCHEMA,
			json_schema_name: GREEN_FEES_FIELD_JSON_SCHEMA_NAME,
			json_schema_strict: false,
		},
	},
	{
		id: "packages-reader",
		label: "Packages reader",
		description:
			"Specialist that extracts stay-and-play package lines + supplements (nights, rounds, board, courses, per-person price) from supplier rate cards. Runs when a rate card's content_types includes \"package\".",
		defaults: {
			model: DEFAULT_PACKAGES_MODEL,
			temperature: 0,
			system_instructions: DEFAULT_PACKAGES_SYSTEM_PROMPT,
			user_instructions: DEFAULT_PACKAGES_USER_PROMPT,
			json_schema: PACKAGES_FIELD_JSON_SCHEMA,
			json_schema_name: PACKAGES_FIELD_JSON_SCHEMA_NAME,
			json_schema_strict: false,
		},
	},
]

export const getToolById = (id) => AI_TOOLS.find((tool) => tool.id === id) || null
